import { gql } from '@apollo/client';
import React from 'react';
import { useModuleTracking } from '../../hooks/useModuleTracking';
import { RestrictPlatform } from '../RestrictPlatform';
import { PumoModuleSlide, PUMO_MODULE_SLIDE_FRAGMENT } from '../PumoModuleSlide';
import { EditorialBgColor, EDITORIAL_BG_COLORS } from '../PumoModuleEditorialText';
import { SinglePumoModuleFragment } from '../../generated/types/SinglePumoModuleFragment';
import { SwiperCarousel } from '../Swiper';

import './SinglePumoModule.scss';

export interface Props {
  singlePumoModule: SinglePumoModuleFragment;
  beforeEyebrow?: React.ReactNode;
  afterEyebrow?: React.ReactNode;
  normalizePumoStyles?: boolean;
  spaceBetween?: boolean;
  children?: React.ReactNode;
}

export const SINGLE_PUMO_MODULE_FRAGMENT = gql`
  fragment SinglePumoModuleFragment on SinglePumoModule {
    id
    name
    bgColor
    id
    platforms: platform
    slides {
      ...PumoModuleSlideFragment
    }
    modelId
    moduleType
  }
  ${PUMO_MODULE_SLIDE_FRAGMENT}
`;

export const SinglePumoModule: React.FC<Props> = ({
  singlePumoModule: { slides, platforms, bgColor, modelId, moduleType, name, id },
  beforeEyebrow,
  afterEyebrow,
  normalizePumoStyles,
  spaceBetween,
  children
}) => {
  const { ref } = useModuleTracking({ name });

  if (slides.length === 0) return null;

  const backgroundColor = EDITORIAL_BG_COLORS[bgColor as EditorialBgColor];

  return (
    <RestrictPlatform platforms={platforms}>
      <div
        className="SinglePumoModule"
        style={{ backgroundColor }}
        data-model-id={modelId}
        data-module-type={moduleType}
        ref={ref}
      >
        {slides.length > 1 ? (
          <SwiperCarousel
            autoplay={{
              delay: 4000,
              disableOnInteraction: false
            }}
            className="SinglePumoModule__carousel"
            classNameNavigation="SinglePumoModule__nav-buttons"
            classNameWrapper="SinglePumoModule__wrapper"
            identifier={`SinglePumoModule__${id}`}
            loop
            loopFillGroupWithBlank
            speed={1400}
          >
            {slides.map((slide, index) => (
              <PumoModuleSlide
                key={index}
                slide={slide}
                beforeEyebrow={beforeEyebrow}
                afterEyebrow={afterEyebrow}
                normalizePumoStyles={normalizePumoStyles}
                spaceBetween={spaceBetween}
              >
                {children}
              </PumoModuleSlide>
            ))}
          </SwiperCarousel>
        ) : (
          <PumoModuleSlide
            slide={slides[0]}
            beforeEyebrow={beforeEyebrow}
            afterEyebrow={afterEyebrow}
            normalizePumoStyles={normalizePumoStyles}
            spaceBetween={spaceBetween}
          >
            {children}
          </PumoModuleSlide>
        )}
      </div>
    </RestrictPlatform>
  );
};
